<script setup lang="ts">
import { ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { taskDefinitionsApi, type TaskDefinition } from '@/api/task-definitions';

interface Props {
  taskDefinition: TaskDefinition;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const submitButtonDisabled = ref(false);

const undiscardMutation = useMutation({
  mutationFn: () => taskDefinitionsApi.update({ id: props.taskDefinition.id }),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  undiscardMutation.mutate();
}
</script>
<template>
  <v-card class="pa-4">
    <v-card-title>
      Restaurar Tarea "{{ taskDefinition.name }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea restaurar la tarea "{{ taskDefinition.name }}"?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        La tarea será restaurada y volverá a estar disponible para crear nuevas tareas.
      </p>
      <v-alert
        v-if="taskDefinition.taskCategoryDiscardedAt"
        type="warning"
        variant="tonal"
        class="mb-4"
      >
        <strong>Nota importante:</strong> La categoría "{{ taskDefinition.taskCategoryName }}"
        también está archivada y será restaurada automáticamente.
      </v-alert>
      <v-alert
        type="info"
        variant="tonal"
        class="mb-4"
      >
        <strong>Categoría:</strong> {{ taskDefinition.taskCategoryName }}<br>
        <strong>Archivada el:</strong> {{ taskDefinition.humanDiscardedAt }}
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="undiscardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="success"
        :loading="undiscardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Restaurar
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="undiscardMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente.
    </v-alert>
  </v-card>
</template>
